import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { FileUp, Loader2, Paperclip, Plus, Send, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, PrimaryButtonClass, Table, Td, Th }
  from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { DateField } from "@/components/common/DateField";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const STATUS = {
  draft: "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300",
  awaiting_approval: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  posted: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};
const LABEL = { draft: "draft", awaiting_approval: "awaiting approval", posted: "posted to ledger",
  rejected: "declined" };

const BLANK = { scope: "programme", programme_code: "", campaign_code: "", cost_centre_code: "",
  work_item_id: "", work_task: "", purchase_item: "", fund_id: "", amount: "", incurred_on: "",
  description: "", payee: "" };

const F = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <Label className="mb-1 block text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export default function ExpenseClaims() {
  const { can } = useAuth();
  const [meta, setMeta] = useState(null);
  const [claims, setClaims] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [warnings, setWarnings] = useState([]);

  const load = useCallback(async () => {
    const [m, c] = await Promise.all([api.get("/expense-claims/meta"), api.get("/expense-claims")]);
    setMeta(m.data);
    setClaims(c.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const selectedItem = (meta?.work_items || []).find((w) => w.id === form.work_item_id);
  const subjectChosen = form.scope === "programme" ? Boolean(form.programme_code)
    : form.scope === "event" ? Boolean(form.campaign_code) : false;
  const eligibleItems = !subjectChosen ? [] : (meta?.work_items || []).filter((w) => (
    form.scope === "programme" ? w.programme_code === form.programme_code
                              : w.campaign_code === form.campaign_code));

  const create = async (confirmDuplicate = false) => {
    setBusy("create");
    try {
      const { data } = await api.post("/expense-claims", {
        ...form, amount: Number(form.amount) || 0, confirm_duplicate: confirmDuplicate,
      });
      toast.success(`${data.claim_ref} saved — attach the bill, then send it for approval`);
      setOpen(false);
      setForm(BLANK);
      setWarnings([]);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.duplicate_warnings) {
        setWarnings(detail.duplicate_warnings);
        toast.warning("This may already have been claimed — check below");
      } else {
        toast.error(apiError(e));
      }
    }
    setBusy("");
  };

  const attach = async (claim, file) => {
    if (!file) return;
    setBusy(claim.claim_ref);
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/expense-claims/${claim.claim_ref}/bill`, body,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill attached");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const submit = async (claim) => {
    setBusy(claim.claim_ref);
    try {
      const { data } = await api.post(`/expense-claims/${claim.claim_ref}/submit`);
      toast.success(`Sent for approval · ${data.approval_ref}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const remove = async (claim) => {
    setBusy(claim.claim_ref);
    try {
      await api.delete(`/expense-claims/${claim.claim_ref}`);
      toast.success(`${claim.claim_ref} deleted`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!meta || !claims) return <Loading label="Loading expense claims" />;
  const c = claims.counters;
  const writable = can("claim", "write");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-041 · Accounts"
        title="Claim back what you spent on our work"
        description="Name the programme or event, point at the task or the item you bought, attach the bill, and it goes to the committee on the same slabs as any other money decision. Once signed off it posts to the ledger and shows in that programme's spend."
        actions={writable && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setWarnings([]); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-claim-button" className={PrimaryButtonClass}>
                <Plus className="mr-1.5 h-4 w-4" /> Raise a claim
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl border-border bg-card">
              <DialogHeader><DialogTitle>Raise an expense claim</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Spent on">
                  <Select value={form.scope}
                          onValueChange={(v) => setForm({ ...form, scope: v, programme_code: "",
                                                          campaign_code: "" })}>
                    <SelectTrigger data-testid="claim-scope-select" className="bg-muted/40">
                      <SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="programme">A programme</SelectItem>
                      <SelectItem value="event">A fundraising campaign or event</SelectItem>
                      <SelectItem value="cost_centre">Everyday running costs</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                {form.scope === "cost_centre" ? (
                  <F label="Cost centre">
                    <Select value={form.cost_centre_code}
                            onValueChange={(v) => setForm({ ...form, cost_centre_code: v,
                                                            work_item_id: "", work_task: "",
                                                            purchase_item: "" })}>
                      <SelectTrigger data-testid="claim-cost-centre-select" className="bg-muted/40">
                        <SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {(meta.cost_centres || []).length === 0 && (
                          <div className="px-2 py-3 text-[11.5px] text-muted-foreground">
                            The Treasurer has not set any up yet
                          </div>
                        )}
                        {(meta.cost_centres || []).map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </F>
                ) : form.scope === "programme" ? (                  <F label="Programme">
                    <Select value={form.programme_code}
                            onValueChange={(v) => setForm({ ...form, programme_code: v })}>
                      <SelectTrigger data-testid="claim-programme-select" className="bg-muted/40">
                        <SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {meta.programmes.map((p) => (
                          <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </F>
                ) : (
                  <F label="Campaign or event">
                    <Select value={form.campaign_code}
                            onValueChange={(v) => setForm({ ...form, campaign_code: v })}>
                      <SelectTrigger data-testid="claim-event-select" className="bg-muted/40">
                        <SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {meta.events.map((e) => (
                          <SelectItem key={e.code} value={e.code}>{e.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </F>
                )}
                <F label="Work item on the board">
                  <Select value={form.work_item_id}
                          onValueChange={(v) => setForm({ ...form, work_item_id: v, work_task: "",
                                                          purchase_item: "" })}>
                    <SelectTrigger data-testid="claim-work-select" className="bg-muted/40">
                      <SelectValue placeholder={subjectChosen ? "Optional" : "Choose the programme or event first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleItems.length === 0 && (
                        <div className="px-2 py-3 text-[11.5px] text-muted-foreground">
                          {subjectChosen ? "Nothing on the board for this yet"
                                         : "Choose the programme or event first"}
                        </div>
                      )}
                      {eligibleItems.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.work_id} · {w.title}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Task on that item">
                  <Select value={form.work_task} disabled={!selectedItem}
                          onValueChange={(v) => setForm({ ...form, work_task: v })}>
                    <SelectTrigger data-testid="claim-task-select" className="bg-muted/40">
                      <SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      {(selectedItem?.tasks || []).map((t) => (
                        <SelectItem key={t.index} value={t.name}>{t.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Item purchased">
                  <Select value={form.purchase_item} disabled={!selectedItem}
                          onValueChange={(v) => setForm({ ...form, purchase_item: v })}>
                    <SelectTrigger data-testid="claim-item-select" className="bg-muted/40">
                      <SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      {(selectedItem?.purchase_items || []).map((i) => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Paid from which fund">                  <Select value={form.fund_id} onValueChange={(v) => setForm({ ...form, fund_id: v })}>
                    <SelectTrigger data-testid="claim-fund-select" className="bg-muted/40">
                      <SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>
                      {meta.funds.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.code} · {money(f.balance, f.currency, true)}
                        </SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Amount">
                  <Input type="number" value={form.amount} data-testid="claim-amount-input"
                         onChange={(e) => setForm({ ...form, amount: e.target.value })}
                         className="bg-muted/40 font-mono" />
                </F>
                <F label="Date it was spent">
                  <DateField value={form.incurred_on} testId="claim-date-input"
                             onChange={(v) => setForm({ ...form, incurred_on: v })} />
                </F>
                <F label="Paid to" >
                  <Input value={form.payee} data-testid="claim-payee-input"
                         onChange={(e) => setForm({ ...form, payee: e.target.value })}
                         className="bg-muted/40" />
                </F>
                <F label="What was it for" full>
                  <Textarea rows={2} value={form.description} data-testid="claim-description-input"
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="bg-muted/40 text-[12px]" />
                </F>
              </div>

              {warnings.length > 0 && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3"
                     data-testid="claim-duplicate-warning">
                  <div className="mb-1.5 flex items-center gap-2 text-[12px] font-semibold text-amber-700 dark:text-amber-300">
                    <TriangleAlert className="h-4 w-4" /> This may already have been claimed
                  </div>
                  {warnings.map((w) => (
                    <p key={w} className="text-[11.5px] text-amber-700/90 dark:text-amber-200/90">{w}</p>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button onClick={() => create(warnings.length > 0)} disabled={busy === "create"}
                        data-testid="claim-save-button" className={PrimaryButtonClass}>
                  {busy === "create" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {warnings.length > 0 ? "Claim it anyway" : "Save claim"}
                </Button>
                <span className="text-[11px] text-muted-foreground">
                  Attach the bill on the next screen, then send it for approval.
                </span>
              </div>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="kpi-claims-draft" label="Drafts" value={c.draft} />
        <Kpi testId="kpi-claims-awaiting" label="Awaiting approval" value={c.awaiting} accent="amber" />
        <Kpi testId="kpi-claims-posted" label="Posted to ledger" value={c.posted} accent="emerald" />
        <Kpi testId="kpi-claims-value" label="Reimbursed" accent="teal"
             value={money(c.posted_value, "INR", true)} />
      </div>

      <Panel testId="panel-claims" kicker="FR-FIN-014" title="Expense claims">
        <Table testId="claims-table"
               head={<><Th>Claim</Th><Th>Spent on</Th><Th>Task / item</Th><Th>Amount</Th>
                 <Th>Date</Th><Th>Bill</Th><Th>Status</Th><Th /></>}>
          {claims.items.length === 0 && <Empty colSpan={8} label="No expense claims yet" />}
          {claims.items.map((row) => (
            <tr key={row.claim_ref} className="border-b border-border/60"
                data-testid={`claim-row-${row.claim_ref}`}>
              <Td>
                <div className="font-mono text-[11px] text-foreground">{row.claim_ref}</div>
                <div className="text-[11px] text-muted-foreground">{row.description}</div>
              </Td>
              <Td className="text-[12px]">{row.subject_label}</Td>
              <Td className="text-[11.5px] text-muted-foreground">
                {row.work_task || row.purchase_item || "—"}
              </Td>
              <Td className="font-mono text-[11.5px]">{money(row.amount, row.currency)}</Td>
              <Td className="font-mono text-[10.5px]">{shortDate(row.incurred_on)}</Td>
              <Td>
                {row.bill ? (
                  <a href={`${BACKEND_URL}/api/expense-claims/${row.claim_ref}/bill`}
                     target="_blank" rel="noreferrer" data-testid={`claim-bill-${row.claim_ref}`}
                     className="inline-flex items-center text-[11px] text-primary hover:underline">
                    <Paperclip className="mr-1 h-3 w-3" /> bill
                  </a>
                ) : <span className="text-[11px] text-muted-foreground">none</span>}
              </Td>
              <Td>
                <Chip className={STATUS[row.status]}>{LABEL[row.status]}</Chip>
                {row.approval_ref && (
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                    {row.approval_ref}
                  </div>
                )}
              </Td>
              <Td className="w-[210px]">
                {writable && row.status === "draft" && (
                  <div className="flex items-center gap-1.5">
                    <label className="inline-flex cursor-pointer items-center rounded-full border border-border px-2.5 py-1 text-[11px] text-foreground/80 hover:border-primary/60">
                      <FileUp className="mr-1 h-3 w-3" /> Bill
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
                             data-testid={`claim-bill-input-${row.claim_ref}`}
                             onChange={(e) => attach(row, e.target.files?.[0])} />
                    </label>
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]"
                            data-testid={`claim-submit-${row.claim_ref}`}
                            disabled={busy === row.claim_ref} onClick={() => submit(row)}>
                      <Send className="mr-1 h-3 w-3" /> Send
                    </Button>
                    <Button size="sm" variant="outline"
                            className="h-7 rounded-full border-red-500/30 text-[11px] text-red-600 dark:text-red-300"
                            data-testid={`claim-delete-${row.claim_ref}`}
                            disabled={busy === row.claim_ref} onClick={() => remove(row)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}
